import { useState, useEffect } from "react";
import { differenceInCalendarDays } from "date-fns";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Pencil, Plus, X } from "lucide-react";

// Iconic top-of-trip stat strip: auto Countries / Days / Flights + up to 2 admin custom tiles.
export default function TripStatsStrip({ config, countries = [], entries = [], isAdmin, onSaved }) {
  const [editing, setEditing] = useState(false);
  const start = config?.start_date ? new Date(config.start_date) : null;
  const end = config?.end_date ? new Date(config.end_date) : null;
  const days = start && end ? differenceInCalendarDays(end, start) + 1 : null;
  const flights = (entries || []).filter((e) => e.entry_type === "flight").length;
  const tiles = Array.isArray(config?.stat_tiles) ? config.stat_tiles.filter((t) => t && t.label) : [];

  const auto = [
    { label: "Countries", value: countries.length },
    ...(days ? [{ label: days === 1 ? "Day" : "Days", value: days }] : []),
    ...(flights ? [{ label: flights === 1 ? "Flight" : "Flights", value: flights }] : []),
  ];
  const all = [...auto, ...tiles.map((t) => ({ label: t.label, value: t.value }))];

  return (
    <div className="rounded-2xl p-5 text-white shadow-[0_8px_32px_rgba(44,44,62,0.12)] relative" style={{ background: "linear-gradient(135deg,var(--brand-navy),var(--brand-navy-dark))" }} data-testid="trip-stats-strip">
      {isAdmin && (
        <button data-testid="edit-stats-btn" onClick={() => setEditing(true)} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors" title="Edit custom stats">
          <Pencil className="h-4 w-4 text-white" />
        </button>
      )}
      {config?.trip_name && <div className="font-heading font-bold text-2xl pr-8">{config.trip_name}</div>}
      <div className="grid grid-cols-3 gap-y-4 gap-x-2 mt-4">
        {all.map((t, i) => (
          <div key={i} className="text-center" data-testid="trip-stat-tile">
            <div className="font-heading font-bold text-3xl leading-none" style={{ color: "var(--tenant-accent-light)" }}>{t.value}</div>
            <div className="text-[11px] uppercase tracking-wide opacity-80 mt-1">{t.label}</div>
          </div>
        ))}
      </div>
      {isAdmin && <StatsEditDialog open={editing} onClose={() => setEditing(false)} tiles={tiles} onSaved={onSaved} />}
    </div>
  );
}

function StatsEditDialog({ open, onClose, tiles, onSaved }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setRows(tiles.length ? tiles.map((t) => ({ label: t.label, value: String(t.value) })) : []);
  }, [open, tiles]);
  const add = () => setRows((r) => (r.length >= 2 ? r : [...r, { label: "", value: "" }]));
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const rm = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      const clean = rows.filter((r) => r.label.trim() && String(r.value).trim())
        .map((r) => ({ label: r.label.trim(), value: String(r.value).trim() }));
      await api.put("/config", { stat_tiles: clean });
      await onSaved();
      toast.success("Stats updated");
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Custom trip stats</DialogTitle>
          <DialogDescription className="text-xs">Countries, Days and Flights are counted automatically. Add up to 2 of your own tiles (e.g. "Conferences · 4", "Mission · 1").</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="flex-1"><Label className="text-xs text-muted-foreground">Label</Label><Input data-testid={`stat-label-${i}`} value={r.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Conferences" className="rounded-xl h-11 mt-1" /></div>
              <div className="w-24"><Label className="text-xs text-muted-foreground">Number</Label><Input data-testid={`stat-value-${i}`} value={r.value} onChange={(e) => upd(i, "value", e.target.value)} placeholder="4" className="rounded-xl h-11 mt-1" /></div>
              <button data-testid={`stat-remove-${i}`} onClick={() => rm(i)} className="h-11 w-9 flex items-center justify-center text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
            </div>
          ))}
          {rows.length < 2 && <Button data-testid="stat-add-btn" onClick={add} variant="outline" className="rounded-full w-full border-dashed"><Plus className="h-4 w-4 mr-1" /> Add stat tile</Button>}
          <Button data-testid="stat-save-btn" disabled={busy} onClick={save} className="w-full rounded-full h-11 accent-bg text-white hover:opacity-90">{busy ? "Saving…" : "Save stats"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
